<script setup lang="ts">
// src/SideWinder.Web/src/components/strategy/UserAssignmentsPanel.vue
//
// Task 8 (user-level strategy assignment SPA): lists every user's current
// strategy assignment (GET /api/admin/strategy/users) and lets a super-user
// switch a user's active strategy via the "Assign" modal (PUT
// /api/admin/strategy/users/{userId}). "History" expands the per-user
// assignment history (GET /api/admin/strategy/users/{userId}/history)
// inline below the row.
import { ref, computed, watch, onMounted } from 'vue'
import { api, type UserStrategyRow, type StrategyCatalogEntry, type UserStrategyHistoryEntry } from '../../api/client'

const users = ref<UserStrategyRow[]>([])
const library = ref<StrategyCatalogEntry[]>([])
const loading = ref(true)
const loadError = ref<string | null>(null)

async function loadData() {
  loading.value = true
  loadError.value = null
  try {
    const [u, lib] = await Promise.all([api.adminStrategyUsers(), api.adminStrategyLibrary()])
    users.value = u
    library.value = lib
  } catch (e: any) {
    loadError.value = e?.message ?? 'Failed to load user assignments'
  } finally {
    loading.value = false
  }
}
onMounted(loadData)

// ── Assign modal ──
const assignModalFor = ref<UserStrategyRow | null>(null)
const assignForm = ref<{ strategyId: string; paramsText: string; reason: string }>({
  strategyId: '', paramsText: '{}', reason: '',
})
const assigning = ref(false)
const assignError = ref<string | null>(null)

// 2026-10-05 (Paolo): moving a user from a legacy strategy onto the shared pot. Their allocation rows
// have no tiers and the allocation card shows tiers only to shared-pot users, so the dialog collects
// them and the API writes them with the assignment. JAPAN is not listed: a user without a JAPAN row
// gets it (weight + tier) from the allocation card after the assignment.
const SHARED_POT_ID = 'shared_pot_v1'
const TIER_FUNDS = ['BGA', 'JH', 'GOLD', 'GGE'] as const
const LIVE_SHARED_POT_PARAMS = { mode: 'shared_pot', shadow: false, evict: false }
const assignTiers = ref<Record<string, string | number>>({})
const isSharedPot = computed(() => assignForm.value.strategyId === SHARED_POT_ID)

watch(() => assignForm.value.strategyId, (id, prev) => {
  if (id !== SHARED_POT_ID || prev === undefined || prev === SHARED_POT_ID) return
  assignForm.value.paramsText = JSON.stringify(LIVE_SHARED_POT_PARAMS, null, 2)
})

function openAssignModal(u: UserStrategyRow) {
  assignModalFor.value = u
  assignForm.value = {
    strategyId: u.strategy_id,
    paramsText: JSON.stringify(u.params ?? {}, null, 2),
    reason: '',
  }
  assignTiers.value = {}
  assignError.value = null
}

function closeAssignModal() {
  assignModalFor.value = null
  assignError.value = null
}

async function submitAssign() {
  if (!assignModalFor.value) return
  assignError.value = null

  if (!assignForm.value.strategyId) {
    assignError.value = 'Strategy is required'
    return
  }

  let parsedParams: Record<string, unknown>
  try {
    parsedParams = assignForm.value.paramsText.trim() ? JSON.parse(assignForm.value.paramsText) : {}
  } catch {
    assignError.value = 'Params must be valid JSON'
    return
  }

  const tiers: Record<string, number> = {}
  if (isSharedPot.value) {
    for (const f of TIER_FUNDS) {
      const raw = String(assignTiers.value[f] ?? '').trim()
      if (!raw) continue
      const t = Number(raw)
      if (!Number.isInteger(t) || t < 1 || t > 9) {
        assignError.value = `${f} tier must be a whole number from 1 to 9`
        return
      }
      tiers[f] = t
    }
  }

  assigning.value = true
  try {
    await api.adminStrategySwitch(assignModalFor.value.user_id, {
      strategy_id: assignForm.value.strategyId,
      params: parsedParams,
      reason: assignForm.value.reason.trim() || null,
      ...(Object.keys(tiers).length > 0 ? { tiers } : {}),
    })
    assignModalFor.value = null
    await loadData()
  } catch (e: any) {
    assignError.value = e?.message ?? 'Failed to switch strategy'
  } finally {
    assigning.value = false
  }
}

// ── Inline history expand ──
const historyOpenFor = ref<string | null>(null)
const historyByUser = ref<Record<string, UserStrategyHistoryEntry[]>>({})
const historyLoading = ref<string | null>(null)

async function toggleHistory(u: UserStrategyRow) {
  if (historyOpenFor.value === u.user_id) {
    historyOpenFor.value = null
    return
  }
  historyOpenFor.value = u.user_id
  if (!historyByUser.value[u.user_id]) {
    historyLoading.value = u.user_id
    try {
      historyByUser.value[u.user_id] = await api.adminStrategyUserHistory(u.user_id)
    } catch {
      historyByUser.value[u.user_id] = []
    } finally {
      historyLoading.value = null
    }
  }
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
  } catch {
    return iso
  }
}
</script>

<template>
  <div class="card">
    <div class="card-header">
      User Assignments
      <span class="header-count">{{ users.length }}</span>
    </div>
    <div class="card-body">
      <div v-if="loading" class="loading-text">Loading user assignments…</div>
      <div v-else-if="loadError" class="msg-error">{{ loadError }}</div>
      <div v-else-if="users.length === 0" class="empty-text">No users found</div>
      <div v-else class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Strategy</th>
              <th>Assigned By</th>
              <th>Assigned At</th>
              <th>Reason</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="u in users" :key="u.user_id">
              <tr>
                <td data-testid="assignment-user">{{ u.display_name || u.email }}<span v-if="u.is_viewer" class="muted-tag"> (linked)</span></td>
                <td class="mono-cell" data-testid="assignment-strategy">{{ u.strategy_id }}<span v-if="u.has_assignment === false" class="muted-tag"> (default)</span></td>
                <td>{{ u.assigned_by }}</td>
                <td class="date-cell">{{ fmtDateTime(u.assigned_at) }}</td>
                <td class="error-cell">{{ u.reason ?? '—' }}</td>
                <td class="action-cell">
                  <button class="btn-secondary-sm btn-assign" @click="openAssignModal(u)">Assign</button>
                  <button class="btn-secondary-sm btn-history" @click="toggleHistory(u)">
                    {{ historyOpenFor === u.user_id ? 'Hide history' : 'History' }}
                  </button>
                </td>
              </tr>
              <tr v-if="historyOpenFor === u.user_id" class="history-row">
                <td colspan="6">
                  <div v-if="historyLoading === u.user_id" class="loading-text">Loading history…</div>
                  <div v-else-if="(historyByUser[u.user_id]?.length ?? 0) === 0" class="empty-text">No prior assignments</div>
                  <table v-else class="data-table history-table">
                    <thead>
                      <tr>
                        <th>Strategy</th>
                        <th>Effective From</th>
                        <th>Effective To</th>
                        <th>Assigned By</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(h, idx) in historyByUser[u.user_id]" :key="idx">
                        <td class="mono-cell">{{ h.strategy_id }}</td>
                        <td class="date-cell">{{ fmtDateTime(h.effective_from) }}</td>
                        <td class="date-cell">{{ h.effective_to ? fmtDateTime(h.effective_to) : 'active' }}</td>
                        <td>{{ h.assigned_by }}</td>
                        <td class="error-cell">{{ h.reason ?? '—' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Assign modal -->
    <div
      v-if="assignModalFor"
      class="modal-backdrop"
      @click.self="closeAssignModal"
      @keydown.esc="closeAssignModal"
    >
      <div class="modal">
        <h3 class="modal-title">Assign strategy — {{ assignModalFor.display_name || assignModalFor.email }}</h3>
        <div class="modal-field">
          <label class="modal-label">Strategy</label>
          <select v-model="assignForm.strategyId" class="modal-select">
            <option v-for="s in library" :key="s.strategy_id" :value="s.strategy_id">
              {{ s.display_name }} ({{ s.strategy_id }})
            </option>
          </select>
        </div>
        <div class="modal-field">
          <label class="modal-label">Params (JSON)</label>
          <textarea v-model="assignForm.paramsText" class="modal-textarea" rows="6"></textarea>
        </div>
        <div v-if="isSharedPot" class="modal-field" data-testid="assign-tiers">
          <label class="modal-label">Tiers (1 = filled first)</label>
          <div class="tier-grid">
            <label v-for="f in TIER_FUNDS" :key="f" class="tier-cell">
              <span>{{ f }}</span>
              <input v-model="assignTiers[f]" type="number" min="1" max="9" step="1" class="tier-input"
                :data-testid="`assign-tier-${f}`" />
            </label>
          </div>
          <span class="muted-tag">Needed for every fund that is on with a weight. Set weights and JAPAN in the allocation table after assigning.</span>
        </div>
        <div class="modal-field">
          <label class="modal-label">Reason</label>
          <input v-model="assignForm.reason" type="text" class="modal-input" placeholder="Why is this switch happening?" />
        </div>
        <div v-if="assignError" class="msg-error">{{ assignError }}</div>
        <div class="modal-actions">
          <button class="btn-secondary-sm" :disabled="assigning" @click="closeAssignModal">Cancel</button>
          <button class="btn-secondary-sm btn-submit-assign" :disabled="assigning" @click="submitAssign">
            {{ assigning ? '…' : 'Submit' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.card {
  background: #161b22;
  border: 1px solid #21262d;
  border-radius: 8px;
  overflow: hidden;
}
.card-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1rem;
  background: #1c2128;
  border-bottom: 1px solid #21262d;
  font-size: 0.85rem;
  font-weight: 600;
  color: #e6edf3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.header-count {
  font-size: 0.75rem;
  font-weight: 400;
  color: #6e7681;
  background: #21262d;
  padding: 1px 6px;
  border-radius: 10px;
}
.card-body { padding: 0.75rem 1rem; }

.table-wrap { overflow-x: auto; }
.data-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
.data-table th {
  text-align: left;
  padding: 6px 10px;
  font-size: 0.7rem;
  color: #6e7681;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #21262d;
  white-space: nowrap;
}
.data-table td { padding: 7px 10px; border-bottom: 1px solid #161b22; vertical-align: middle; color: #c9d1d9; }
.data-table tbody tr:hover { background: #1c2128; }
.data-table tbody tr:last-child td { border-bottom: none; }

.history-row td { background: #0d1117; }
.history-table { margin-top: 0.4rem; }

.mono-cell { font-family: 'JetBrains Mono', monospace; font-size: 0.8rem; }
.date-cell { white-space: nowrap; font-size: 0.8rem; color: #8b949e; }
.action-cell { text-align: right; white-space: nowrap; }
.error-cell {
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.78rem;
}

.loading-text, .empty-text { padding: 0.75rem 0; color: #6e7681; font-size: 0.9rem; font-style: italic; }
.msg-error {
  margin-top: 0.5rem;
  padding: 0.4rem 0.75rem;
  background: rgba(248,81,73,0.1);
  border: 1px solid #f85149;
  border-radius: 4px;
  font-size: 0.85rem;
  color: #f85149;
}

.btn-secondary-sm {
  padding: 3px 10px;
  margin-right: 0.4rem;
  background: transparent;
  border: 1px solid #58a6ff;
  border-radius: 4px;
  color: #58a6ff;
  font-size: 0.75rem;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.btn-secondary-sm:hover:not(:disabled) { background: rgba(88,166,255,0.15); }
.btn-secondary-sm:disabled { opacity: 0.5; cursor: not-allowed; }

/* Modal */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.modal {
  background: #161b22;
  border: 1px solid #30363d;
  border-radius: 8px;
  padding: 1.25rem 1.5rem;
  width: min(480px, 90vw);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}
.modal-title { margin: 0; font-size: 0.95rem; font-weight: 600; color: #e6edf3; }
.modal-field { display: flex; flex-direction: column; gap: 0.25rem; }
.muted-tag { color: #8b949e; font-size: 0.75rem; }
.tier-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 0.5rem; }
.tier-cell { display: flex; flex-direction: column; gap: 0.15rem; font-size: 0.75rem; color: #8b949e; }
.tier-input { width: 100%; background: #0d1117; border: 1px solid #30363d; color: inherit; border-radius: 4px; padding: 0.3rem; }
.modal-label { font-size: 0.78rem; color: #6e7681; text-transform: uppercase; letter-spacing: 0.4px; }
.modal-select, .modal-input, .modal-textarea {
  padding: 0.4rem 0.6rem;
  background: #0d1117;
  border: 1px solid #30363d;
  border-radius: 4px;
  color: #c9d1d9;
  font-size: 0.88rem;
  outline: none;
  transition: border-color 0.15s;
  font-family: inherit;
}
.modal-textarea { font-family: 'JetBrains Mono', monospace; font-size: 0.8rem; resize: vertical; }
.modal-select:focus, .modal-input:focus, .modal-textarea:focus { border-color: #58a6ff; }
.modal-input::placeholder { color: #484f58; }
.modal-actions { display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 0.25rem; }

@media (max-width: 600px) {
  .data-table th, .data-table td { padding: 5px 6px; }
}
</style>
